<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDatasources, useDatasourceTypes } from "@/api/admin";
import DatasourceWizard from "@/components/admin/DatasourceWizard.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import { t } from "@/copy/strings";

definePage({ meta: { title: "New datasource", requires: { adminArea: "datasources" } } });

const route = useRoute();
const router = useRouter();
const types = useDatasourceTypes();
const datasources = useDatasources();
const lineages = computed(() => [...new Set((datasources.data.value ?? []).map((d) => d.labels.lineage).filter(Boolean) as string[])].sort());
const queryString = (key: string) => (typeof route.query[key] === "string" ? (route.query[key] as string) : undefined);
</script>

<template>
  <div>
    <PageHeader
      :title="t.admin.newDatasource"
      description="Guided setup: what to prepare, the commands to create a least-privilege account, the connection, labels and who gets access."
      :crumbs="[{ label: t.admin.title, to: '/admin' }, { label: t.admin.datasources, to: '/admin/datasources' }, { label: t.common.create }]"
    />
    <ListSkeleton v-if="types.isPending.value || datasources.isPending.value" :rows="4" />
    <ErrorState v-else-if="types.error.value" :error="types.error.value" @retry="types.refetch()" />
    <DatasourceWizard
      v-else-if="types.data.value"
      :types="types.data.value"
      :datasources="datasources.data.value ?? []"
      :initial-type="queryString('type')"
      :initial-import="queryString('import')"
      :lineages="lineages"
      @cancel="router.push('/admin/datasources')"
    />
  </div>
</template>
